import ContentsToReact from '@/components/ContentsToReact/ContentsToReact';
import GenericModals from '@/components/GenericModals/GenericModals';
import PageHead from '@/components/PageHead/PageHead';
import { MagazineContextProvider } from '@/contexts/Magazine/Magazine.context';
import { MagazineArticlesContextProvider } from '@/contexts/Magazine/MagazineArticles.context';
import { MagazineContextProviderProps } from '@/contexts/Magazine/interfaces';
import { extractContents } from '@/helpers/extractContents';
import extractPageMetaFromRouteData from '@/helpers/extractPageMetaFromRouteData';
import { useInitialPageDataLayer } from '@/hooks/useInitialPageDataLayer/useInitialPageDataLayer';
import useMagazineArticles from '@/hooks/useMagazineArticles/useMagazineArticles';
import useMagazineFilterArticles from '@/hooks/useMagazineFilterArticles/useMagazineFilterArticles';
import { useSelected } from '@/hooks/useSelected/useSelected';
import type { PageModel } from '@/models/page';
import Container from '@uikit/components/Container/Container';
import EnercityFooter from '@uikit/components/EnercityFooter/EnercityFooter';
import Link from '@uikit/components/Link/Link';
import MagazineCategoryHero from '@uikit/components/Magazine/MagazineCategoryHero/MagazineCategoryHero';
import MagazineLayout from '@uikit/components/Magazine/MagazineLayout/MagazineLayout';
import MagazineLogoMobile from '@uikit/components/Magazine/MagazineLogoMobile/MagazineLogoMobile';
import MagazineLogoMobileStyles from '@uikit/components/Magazine/MagazineLogoMobile/MagazineLogoMobile.module.scss';
import MagazineMain from '@uikit/components/Magazine/MagazineMain/MagazineMain';
import MagazineTagNavigation from '@uikit/components/Magazine/MagazineTagNavigation/MagazineTagNavigation';
import MagazineGridWrapper from '@uikit/components/MagazineGridWrapper/MagazineGridWrapper';
import NewHeader from '@uikit/components/NewHeader/NewHeader';
import PageHeader from '@uikit/components/NewHeader/PageHeader/PageHeader';
import PromoBanner from '@uikit/components/PromoBanner/PromoBanner';
import Section from '@uikit/components/Section/Section';
import clsx from 'clsx';
import React from 'react';
import { CmsComponentsContext } from '../CmsComponentsContext';
import { MAGAZINE_HEADER_NAV_ITEMS } from '../MagazineMainPage/consts';
import cmsComponents from './MagazineCategoryPage.cmsComponents';
import type { MagazineCategoryPageProps } from './interfaces';

const MagazineCategoryPage = (
  routeData: PageModel<MagazineCategoryPageProps>
): JSX.Element => {
  const {
    contents,
    modals,
    props: {
      image,
      subTitle,
      title,
      publicationDate,
      category,
      tags = [],
      ogImage,
    },
  } = routeData;

  const meta = extractPageMetaFromRouteData(routeData, {
    ogImage: ogImage ?? {
      name: 'Magazine Logo',
      path: '/assets/magazin/enercity-magazine-logo-og.png',
    },
  });

  const { data = [] } = useMagazineArticles();

  const {
    extractedContents: [additionalContents, magazineGrid],
    restContents,
  } = extractContents(
    ['magazine-additional-contents', 'magazine-grid'],
    contents
  );

  const articles = React.useMemo(() => {
    const [magazineGridNode] = magazineGrid ?? [];
    const articleIdsFromMagazineGridArea =
      magazineGridNode?.contents?.map((item) => item?.props?.article?.id) ?? [];
    return data.filter(
      (article) => !articleIdsFromMagazineGridArea.includes(article.id)
    );
  }, [data, magazineGrid]);

  const {
    selected: selectedTags,
    toggleSelected: toggleSelectedTag,
    isSelected: isSelectedTag,
  } = useSelected([]);
  const { filteredArticles } = useMagazineFilterArticles(
    articles,
    category,
    selectedTags
  );

  const magazineArticleContextValue = React.useMemo(() => {
    const value: MagazineContextProviderProps['value'] = {
      currentCategory: category,
    };

    if (publicationDate) {
      value.publicationDate = new Date(publicationDate);
    }

    return value;
  }, [category, publicationDate]);

  const showRestContents = !selectedTags.length && restContents;

  useInitialPageDataLayer();

  return (
    <CmsComponentsContext.Provider value={cmsComponents}>
      <PageHead {...meta} />

      <MagazineLayout>
        <MagazineContextProvider value={magazineArticleContextValue}>
          <NewHeader
            hasPageHeader
            isFullWidth
            isInverted
            isSticky
            renderPageHeader={(props) => (
              <PageHeader
                {...props}
                links={MAGAZINE_HEADER_NAV_ITEMS}
                renderTitle={({
                  hasDropdown,
                  isDropdownActive,
                  isInverted,
                  isTranslucent,
                  shouldHaveBackground,
                  toggleDropdown,
                }) => {
                  const content = (
                    <MagazineLogoMobile
                      className={
                        isInverted && !isTranslucent && !shouldHaveBackground
                          ? MagazineLogoMobileStyles.colorWhite
                          : MagazineLogoMobileStyles.colorSurrogate
                      }
                      width={90}
                    />
                  );

                  return hasDropdown ? (
                    <PageHeader.Activator
                      className={clsx({
                        [MagazineLogoMobileStyles.colorSurrogate]:
                          shouldHaveBackground,
                      })}
                      isActive={isDropdownActive}
                      isEnabled={hasDropdown}
                      isInverted={isInverted}
                      onClick={toggleDropdown}
                    >
                      {content}
                    </PageHeader.Activator>
                  ) : (
                    <Link style={{ display: 'flex' }} to="/magazin">
                      {content}
                      <span className="visually-hidden">
                        Zurück zum Magazin
                      </span>
                    </Link>
                  );
                }}
              />
            )}
            variant="enercity"
          />

          <MagazineMain>
            <MagazineContextProvider value={magazineArticleContextValue}>
              <MagazineCategoryHero
                title={title}
                subTitle={subTitle}
                image={image}
              />
            </MagazineContextProvider>
            <MagazineArticlesContextProvider articles={filteredArticles}>
              <Section>
                <Container>
                  <MagazineTagNavigation
                    tags={tags}
                    toggleSelectedTag={toggleSelectedTag}
                    isSelectedTag={isSelectedTag}
                  />
                </Container>
              </Section>

              {!!selectedTags.length && (
                <Section>
                  <Container>
                    <MagazineGridWrapper />
                  </Container>
                </Section>
              )}

              {showRestContents && <ContentsToReact contents={magazineGrid} />}
              {showRestContents && <ContentsToReact contents={restContents} />}
            </MagazineArticlesContextProvider>

            {additionalContents && (
              <ContentsToReact contents={additionalContents} />
            )}
          </MagazineMain>
          <EnercityFooter />
        </MagazineContextProvider>
      </MagazineLayout>
      <GenericModals modals={modals} />
      {routeData.props.promoBannerVisible === 'promoBanner' &&
        routeData.props.promoBanner && (
          <PromoBanner banner={routeData.props.promoBanner} />
        )}
    </CmsComponentsContext.Provider>
  );
};

export default MagazineCategoryPage;
